// System
import { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { TableColumnHeader } from '@Components/shared/tables/columns';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';

// Utils
import {
  TransactionExportsListColumns,
  TransactionExportsListFilter,
  transactionExportsListColumns,
} from '@utils/tables/transactionExports/transactionExportsListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { parseISO } from 'date-fns';
import { TransactionExportsDto } from '@contracts/api/dto/transaction/TransactionExportsDto';

type HeaderOptions = {
  filter: Filterable<TransactionExportsListFilter>;
};

type Columns = TableColumnDescriptionArray<
  TransactionExportsDto,
  HeaderOptions,
  typeof transactionExportsListColumns
>;

type Column = TableColumnDescription<
  TransactionExportsDto,
  HeaderOptions,
  TransactionExportsListColumns
>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useTransactionExportsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'transactionExports._id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>{row?._id && <TruncateText text={row?._id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'transactionExports.exportType',
          title: 'Export Type',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.exportType}</Box>,
        },
        {
          name: 'transactionExports.status',
          title: 'Status',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.status && <Box sx={{ ...ellipsis }}>{row?.status}</Box>}
            </Box>
          ),
        },
        {
          name: 'transactionExports.createdAt',
          title: 'Created At',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'transactionExports.finishedOn',
          title: 'Finished On',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.finishedOn && (
                <DateLabel date={new Date(parseISO(row?.finishedOn))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'transactionExports.filename',
          title: 'File Name',
          sx: { minWidth: '300px', maxWidth: '300px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.filename}</Box>,
        },
        {
          name: 'transactionExports.download',
          title: 'Download',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              <Button
                variant="contained"
                color="primary"
                disabled={row?.status !== 'READY'}
                onClick={() => window.open(row?.url, '_blank')}
              >
                Download
              </Button>
            </Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
